/*
 * Copyright (C) 2024 S&P Global.
 * All Rights Reserved
 */
import { AppIdentifier } from "@finos/fdc3";
import { AppType } from "../types";
import { AppRefManager } from "../lifecycle";
import { FusionInteropMessage, MessageListener, MessageType, SystemMessagePayload, TargetableWindow } from "../types/message";
import { getAppType } from "./getAppType";

export class MessageService {
  public readonly browserGlobalScope: Window & typeof globalThis;
  public readonly sourceAppType: AppType;

  private _appRefManager: AppRefManager;
  // private _childAppIframeElmt: HTMLIFrameElement | undefined;
  // private _childAppTargetOrigin: string | undefined;
  private _parentAppWindow: Window | undefined;
  // private _parentAppTargetOrigin: string | undefined;

  // prettier-ignore
  constructor(browserGlobalScope: Window & typeof globalThis,
              appRefManager: AppRefManager) {
    console.log("MessageService.constructor");
    if (!appRefManager) {
      throw new Error("appRefManager not provided");
    }
    this.browserGlobalScope = browserGlobalScope;
    this._appRefManager = appRefManager;
    this.sourceAppType = getAppType(browserGlobalScope);
    console.log(`this.sourceAppType: ${this.sourceAppType}`);
    switch (this.sourceAppType) {
      // case AppType.parent:
      //   //TODO - Extend MessageService to handle multiple child app iframes
      //   this._childAppIframeElmt = this.browserGlobalScope.document.querySelector("iframe") as HTMLIFrameElement;
      //   if (!this._childAppIframeElmt || !this._childAppIframeElmt.contentWindow) {
      //     throw new Error("Child app window could not be located");
      //   }
      //   break;
      case AppType.child:
        if (this.browserGlobalScope.parent === this.browserGlobalScope) {
          throw new Error("Parent app window could not be located");
        }
        this._parentAppWindow = this.browserGlobalScope.parent;
        // this._parentAppTargetOrigin = this._getParentAppTargetOrigin();
        break;
    }
  }

  public publish(message: FusionInteropMessage<SystemMessagePayload>, target: AppType | AppIdentifier | TargetableWindow): void {
    console.log("MessageService.publish");
    if (!message.type || !message.header || !message.payload) {
      throw new Error("Incomplete message provided");
    }
    if (message.type === MessageType.spgiFusionSystem && !(message.payload as SystemMessagePayload).type) {
      throw new Error("System message type is missing from the message payload");
    }
    //TODO - Validate header
    message.header.sentTimestamp = Date.now();
    if (typeof target === "string" && target as string in AppType) {
      // Need to publish to AppType target
      console.log("publish target: AppType");
      if (target === this.sourceAppType) {
        throw new Error("Cannot publish message to self");
      }
      switch (this.sourceAppType) {
        // case AppType.parent:
        //   //TODO: Look at deprecating the ability to publish to the default child app iframe window, as this is not compatible with support for multiple child app iframes
        //   if (target !== AppType.child) {
        //     throw new Error("Parent app can only publish data to child app");
        //   }
        //   //TODO: Actually not sure if _childAppIframeElmt can be tampered with after initialisation. _childAppIframeElmt does not hold a weak ref and only the current value of the Iframe Element when initialised
        //   //TODO: One of the reasons for the check was the theoretical situation where the chrome.exe process for the iframe was unexpectedly terminated e.g. what happens to contentWindow in that scenario?
        //   if (!this._childAppIframeElmt || !this._childAppIframeElmt.contentWindow) {
        //     throw new Error("Child app window not found");
        //   }
        //   // if (!this._childAppTargetOrigin) {
        //   //   throw new Error("Child app target origin not found");
        //   // }
        //   this._childAppIframeElmt.contentWindow.postMessage(message, /*this._childAppTargetOrigin*/"*");
        //   break;
        case AppType.child:
          if (target !== AppType.parent) {
            throw new Error("Child app can only publish data to parent app");
          }
          if (!this._parentAppWindow) {
            throw new Error("Parent app window not found");
          }
          // if (!this._parentAppTargetOrigin) {
          //   throw new Error("Parent app target origin not found");
          // }
          this._parentAppWindow.postMessage(message, /*this._parentAppTargetOrigin*/"*");
          break;
      }
    }
    else if (target && typeof target === "object" && (target as AppIdentifier).appId) {
      // Need to publish to AppIdentifier target
      console.log("publish target: AppIdentifier");
      const targetAppRef = this._appRefManager.getFirstAppReference(target as AppIdentifier);
      if (!targetAppRef) {
        throw new Error("App not found");
      }
      if (targetAppRef.appType === this.sourceAppType) {
        throw new Error("Cannot publish message to self");
      }
      targetAppRef.window.postMessage(message, targetAppRef.origin);
    }
    else if (target && typeof target === "object" && (target as TargetableWindow).origin && (target as TargetableWindow).window && typeof (target as TargetableWindow).window === "object") {
      // Need to publish to TargetableWindow target (i.e. WindowProxy with origin)
      console.log("publish target: TargetableWindow");
      const targetableWindow = target as TargetableWindow;
      if (targetableWindow.window === this.browserGlobalScope) {
        throw new Error("Cannot publish message to self");
      }
      targetableWindow.window.postMessage(message, targetableWindow.origin);
    }
  }

  public addMessageListener(messageListener: MessageListener): void {
    if (!messageListener) {
      throw new Error("messageListener not provided");
    }
    if (typeof messageListener !== "function") {
      throw new Error("messageListener is not a function");
    }
    this.browserGlobalScope.addEventListener("message", messageListener);
    this.browserGlobalScope.addEventListener("pagehide", (event: PageTransitionEvent) => {
      // The consuming app is being torn down, so unregister the message listener
      this.browserGlobalScope.removeEventListener("message", messageListener);
    });
  }

  // private _getParentAppTargetOrigin(): string {
  //   // Child app is running in an iframe that is hosted in the parent app window
  //   // Note that We fallback to document.referrer when location.ancestorOrigins is unsupported.
  //   //TODO - document.referrer has implications in OIDC redirect scenarios in AuthN implementation (see detail in #308508).
  //   let targetOrigin: string;
  //   if (this.browserGlobalScope.location.ancestorOrigins && this.browserGlobalScope.location.ancestorOrigins.length > 0 && this.browserGlobalScope.location.ancestorOrigins[0]) {
  //     targetOrigin = new URL(this.browserGlobalScope.location.ancestorOrigins[0]).origin;
  //   } else if (this.browserGlobalScope.document.referrer) {
  //     targetOrigin = new URL(this.browserGlobalScope.document.referrer).origin;
  //   } else {
  //     throw new Error("Ancestor origins and referrer unexpectedly empty when locating parent app for message publication");
  //   }
  //   return targetOrigin;
  // }
}
